import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFirebaseAuth } from "../firebase/firebaseService";
import { getAuthConfig } from "../config/AuthConfigContext";
import { UserStore } from "../store/userGeneralInfoStore";

/** Default inactivity window before auto sign-out when no override is configured. */
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * Signs the user out after a period of document inactivity (mouse, keyboard, scroll, touch).
 * Timeout resolves from `timeoutMs`, then `AuthWrapper` config `inactivityTimeoutMs`, then default (1 hour).
 *
 * @param timeoutMs - Optional inactivity duration in milliseconds; overrides config when set
 */
export function useInactivityLogout(timeoutMs?: number): void {
  const navigate = useNavigate();

  useEffect(() => {
    const config = getAuthConfig();
    const effectiveTimeout =
      timeoutMs ?? config?.inactivityTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    let inactivityTimer: ReturnType<typeof setTimeout>;

    const handleLogout = () => {
      config?.analytics?.clearUser?.();
      getFirebaseAuth().signOut();
      UserStore.clearAll();
      navigate(config?.authRedirectPath ?? "/auth");
    };

    const resetTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      inactivityTimer = setTimeout(handleLogout, effectiveTimeout);
    };

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [navigate, timeoutMs]);
}
